import type { ComponentType } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from './icons';

/**
 * 통계 타일 — 목록 페이지 상단의 "요약 카드 + 목록 바로가기" 골격.
 *
 * 처음엔 `pages/approvals/index.tsx` 안에 있었고, 멤버 관리가 같은 골격을 필요로 하면서
 * 이 자리로 올렸다(2026-08-02). 아래 두 함수의 주석은 승격 전 그대로다 — 예시가 결재함
 * 카드(내 결재 대기·평균 소요)로 적혀 있는 건 그 때문이고, 규약 자체는 소비처 공통이다.
 * admin도 같은 카드가 필요해지면 그때 `@safetyhub/ui` 승격을 검토한다 — 지금은 web 전용이다.
 */

/**
 * 카드 헤더 우측 pill의 두 형태 — 목업 `.stat-h .pill`.
 * - `diff`: 증감(쉐브론 + 부호 수치, `suffix`로 `%` 등). 0은 넘기지 않는다(보여 줄 변화가
 *   없으면 pill 자체를 띄우지 않는다 — 소비처 규약).
 * - `alert`: 경고 라벨(rose, 쉐브론 없음) — 목업 초대 대기 카드의 `만료 임박 1`.
 */
export type StatTilePill =
  | { kind: 'diff'; diff: number; suffix?: string }
  | { kind: 'alert'; label: string };

export function StatPill({ pill }: { pill: StatTilePill }) {
  if (pill.kind === 'alert') {
    return (
      <span className="inline-flex items-center rounded-full bg-rose-50 px-[7px] py-[2.5px] text-[12.5px] font-bold text-rose-600">
        {pill.label}
      </span>
    );
  }

  const rising = pill.diff > 0;
  const Chevron = rising ? ChevronUpIcon : ChevronDownIcon;
  return (
    <span
      className={`inline-flex items-center gap-[3px] rounded-full px-[7px] py-[2.5px] text-[12.5px] font-bold ${
        rising ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
      }`}
    >
      <Chevron className="h-2.5 w-2.5" />
      {`${rising ? '+' : ''}${pill.diff}${pill.suffix ?? ''}`}
    </span>
  );
}

/**
 * 통계 타일 — 앞 3장은 대응 탭으로 가는 바로가기이고, '평균 소요'는 표시 전용이다.
 * 카드 값은 전부 "내 기준"이라, 선택 표시는 탭만 열려 있다고 붙이지 않고 **목록이 카드와
 * 같은 축일 때만** 붙인다 — 내 결재 대기·오늘 처리 완료는 대응 탭 + '내 결재만 보기'가
 * 함께 켜져 있어야 하고, 내가 제출은 탭 자체가 서버에서 내 기준이라 탭만으로 일치한다.
 *
 * `Card` 프리미티브를 쓰지 않은 이유: Card는 면·테두리를 prop으로 소유해서 선택 상태의
 * 브랜드 면/테두리를 `className`으로 얹을 수 없다(같은 CSS 속성을 다투면 생성 순서가
 * 승자를 정한다 — `Card.tsx` 주석). 그래서 여기서는 타일이 직접 면을 갖고, 클릭 가능한
 * 타일은 div가 아니라 `<button>`으로 낸다(키보드 접근·`aria-pressed` 때문).
 *
 * 골격은 목업 `.stat`이다 — 아이콘+라벨 헤더 / 큰 값+작은 단위 / 보조 문구 3층으로 쌓고,
 * 우상단에 장식 원을 깐다. 상태색을 각 조각에 따로 칠하지 않고 **카드 루트의 `color`로
 * 한 번만 정하는 것**이 이 골격의 핵심이다(`accentClass`): 값 숫자도 장식 원(`bg-current`)도
 * 아이콘 글리프도 그 색을 상속하므로, 카드 성격이 바뀔 때 고칠 자리가 한 곳뿐이다.
 * 라벨·단위·푸터만 회색을 명시해 상속에서 빠져나온다.
 */
export function StatTile({
  label,
  value,
  unit,
  foot,
  pill,
  icon: Icon,
  accentClass,
  iconBgClass,
  selected,
  onSelect,
}: {
  label: string;
  value: string;
  /** 값 뒤에 작게 붙는 단위('건'·'시간'). 값이 '—'처럼 수가 아니면 비운다. */
  unit?: string;
  /** 값 아래 보조 문구 — 없으면 그 줄 자체가 사라진다. */
  foot?: string;
  /** 헤더 우측 pill(증감 또는 경고). 보여 줄 것이 없으면 넘기지 않는다. */
  pill?: StatTilePill;
  icon: ComponentType<{ className?: string }>;
  /** 카드의 상태색 — 루트 `text-*`. 값·장식 원·아이콘이 여기서 색을 물려받는다. */
  accentClass: string;
  /** 아이콘 박스 바탕(상태색 틴트). 글리프 색은 지정하지 않는다(accent 상속). */
  iconBgClass: string;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const body = (
    <>
      {/* 목업 `.stat::after` — 우상단에서 잘려 나가는 상태색 원. 카드가 overflow-hidden이라
          바깥으로 나간 부분이 잘리고, 흐름 밖(absolute)이라 아래 3층 레이아웃을 밀지 않는다.
          이후 콘텐츠에 `relative`를 주는 건 이 원 위로 올리기 위해서다. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-[30px] -top-[30px] h-[120px] w-[120px] rounded-full bg-current opacity-[0.08]"
      />

      <div className="relative mb-3.5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span
            className={`grid h-[34px] w-[34px] flex-none place-items-center rounded-[10px] ${iconBgClass}`}
          >
            <Icon className="h-[17px] w-[17px]" />
          </span>
          <span className="text-[13.5px] font-semibold tracking-[.01em] text-ink-500">{label}</span>
        </div>
        {pill && <StatPill pill={pill} />}
      </div>

      <div className="relative flex items-baseline gap-2">
        <span className="text-[28px] font-extrabold tracking-[-0.04em] tabular-nums">{value}</span>
        {unit && <span className="text-[14.5px] font-semibold text-ink-400">{unit}</span>}
      </div>

      {foot && <p className="relative mt-2 text-[13px] font-medium text-ink-400">{foot}</p>}
    </>
  );

  const base = `relative w-full overflow-hidden rounded-card-lg border px-5 py-[18px] text-left shadow-card-sm ${accentClass}`;

  if (!onSelect) {
    return <div className={`${base} border-line bg-white`}>{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      // hover는 목업대로 살짝 떠오르는 것(translate+shadow)으로 낸다. 면 색을 hover에 쓰지
      // 않는 덕에 선택 면(brand-50)과 다투지 않아, 선택된 카드에 마우스를 올려도 선택
      // 표시가 그대로 남는다.
      className={`${base} transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-brand-500 ${
        selected ? 'border-brand-500 bg-brand-50' : 'border-line bg-white'
      }`}
    >
      {body}
    </button>
  );
}
